import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import { ColorThemeSelector } from "./setting/ColorThemeSelector";
import TemplateSettings from "./setting/TemplateSettings";
import LabelSettings from "./setting/LabelSettings";
import { t } from "i18next";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { saveReportConfig } from "../../services/reportService";
import { useColorTheme } from "./setting/ColorThemeContext";
import { SettingsIcon } from "common-lib";
import { toast } from "react-toastify";
import { OK_LABEL } from "./reportMetrics";

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((v) => b.includes(v));

export default function SettingContent({
  classList,
  config,
  setConfig,
}: {
  classList: string[];
  config: reportConfigItem | null;
  setConfig: (config: reportConfigItem | null) => void;
}) {
  const { id } = useParams();
  const { setSelectedTheme, colorThemes } = useColorTheme();
  // OK 는 불량 클래스가 아니므로 선택 대상에서 제외 (reportMetrics 와 동일 기준)
  const classes = useMemo(() => (classList ?? []).filter((c) => c !== OK_LABEL), [classList]);

  // 저장된 값: class_filter 가 비어있으면(null/[]) 전체
  const savedTheme = config?.color_theme && config.color_theme in colorThemes ? config.color_theme : "default";
  const savedClasses = useMemo(() => {
    const filter = (config?.class_filter ?? []) as string[];
    const valid = filter.filter((c) => classes.includes(c));
    return valid.length ? valid : classes;
  }, [config?.class_filter, classes]);

  const [tempTheme, setTempTheme] = useState(savedTheme);
  const [tempSelectedLabels, setTempSelectedLabels] = useState<string[]>(savedClasses);
  const [isSaving, setIsSaving] = useState(false);

  // config / 클래스 목록이 비동기로 들어오면 편집값을 저장값으로 맞춤
  useEffect(() => setTempTheme(savedTheme), [savedTheme]);
  useEffect(() => setTempSelectedLabels(savedClasses), [savedClasses]);

  const hasChanges = tempTheme !== savedTheme || !sameSet(tempSelectedLabels, savedClasses);
  const canApply = hasChanges && tempSelectedLabels.length > 0 && !isSaving;

  const handleReset = () => {
    setTempTheme("default");
    setTempSelectedLabels(classes);
  };

  const handleApply = async () => {
    if (!tempSelectedLabels.length) return toast.warning(t("MsgMinOneClassRequired"));
    setIsSaving(true);
    try {
      const response = await saveReportConfig({
        id: Number(id),
        color_theme: tempTheme,
        // 전체 선택 = [] → 이후 추가되는 클래스도 자동 포함
        class_filter: sameSet(tempSelectedLabels, classes) ? [] : tempSelectedLabels,
      });
      setConfig({ ...config, ...JSON.parse(response.report_config) });
      setSelectedTheme(tempTheme);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const applyButtonStyle = {
    padding: "6px 14px",
    borderRadius: "8px",
    border: "1px solid #3b82f6",
    background: "#3b82f6",
    fontSize: "12px",
    fontWeight: 600,
    color: "#fff",
    cursor: canApply ? "pointer" : "not-allowed",
    opacity: canApply ? 1 : 0.4,
    transition: "all .15s",
  };

  return (
    <Box sx={{ pb: "80px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsIcon sx={{ fontSize: 16, color: "#6b7280" }} />
          </Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{t("Setting")}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "6px" }}>
          {/* 기본값(default 테마, 전체 클래스)으로 되돌림. 적용 전까지 저장되지 않음 */}
          <button
            onClick={handleReset}
            style={{
              padding: "6px 14px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              background: "#fff",
              fontSize: "12px",
              fontWeight: 500,
              color: "#6b7280",
              cursor: "pointer",
            }}
          >
            {t("AllClear")}
          </button>
          <button disabled={!canApply} onClick={handleApply} style={applyButtonStyle}>
            {t("ApplyChanges")}
          </button>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <ColorThemeSelector tempTheme={tempTheme} setTempTheme={setTempTheme} />
        <LabelSettings
          labelList={classes}
          tempSelectedLabels={tempSelectedLabels}
          setTempSelectedLabels={setTempSelectedLabels}
        />
        <TemplateSettings />
      </Box>
      {hasChanges && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(8px)",
            borderTop: "1px solid #e5e7eb",
            boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
            px: "24px",
            py: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 1200,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#f59e0b" }} />
            <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
              저장되지 않은 변경사항이 있습니다
              {tempSelectedLabels.length === 0 && (
                <span style={{ color: "#ef4444", marginLeft: 8 }}>· {t("MsgMinOneClassRequired")}</span>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => {
                setTempTheme(savedTheme);
                setTempSelectedLabels(savedClasses);
              }}
              style={{
                padding: "7px 18px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontSize: "13px",
                color: "#6b7280",
                cursor: "pointer",
              }}
            >
              {t("Cancel")}
            </button>
            <button disabled={!canApply} onClick={handleApply} style={{ ...applyButtonStyle, padding: "7px 18px", fontSize: "13px" }}>
              {t("ApplyChanges")}
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
